import React, { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import CustomToggle from "@/components/custom-toggle/custom-toggle.tsx"
import { FilterScroll } from "@/components/filter-scroll/filter-scroll.tsx"
import SearchBarGeneral from "@/components/search-bar/search-bar-general.tsx"
import { SelectPill } from "@/components/select-pill/select-pill.tsx"
import { SelectionRowDrawer } from "@/components/selection-row-drawer/selection-row-drawer.tsx"
import {
  ALL_SPECIES,
  ANIMAL_GENDERS,
  ANIMAL_STATUS_ENUM,
  type AnimalLifeSpan,
} from "@/features/farm"
import AnimalAgeDrawer from "@/features/farm/components/animal-age-drawer-filter/animal-age-drawer.tsx"
import useGetAnimalsOptionsWithCount from "@/features/farm/hooks/use-get-animals-options-with-count.ts"
import { useGetSpeciesOptions } from "@/features/farm/hooks/use-get-species-options.ts"
import { type AnimalFilterSelection } from "@/features/farm/utils/animal-filter.ts"

interface FilterTabProps {
  handleSearch?: (searchQuery: string) => void
  handleAgeChange: (ageRange: AnimalLifeSpan) => void
  handleGenderChange: (gender: string) => void
  handleStatusChange: (status: string) => void
  handleSpeciesChange: (species: string) => void
  handleOutOfExploitationChange: (outOfExploitation: boolean) => void
  selections: AnimalFilterSelection
}

export const AnimalFilterTab: React.FC<FilterTabProps> = ({
  handleSearch,
  handleAgeChange,
  handleGenderChange,
  handleSpeciesChange,
  handleStatusChange,
  handleOutOfExploitationChange,
  selections,
}) => {
  const speciesOptionsWithCount = useGetSpeciesOptions()
  const { animalStatusesWithCount, animalGendersWithCount } =
    useGetAnimalsOptionsWithCount()
  const { t } = useTranslation()

  const [filterSelectionState, setFilterSelectionState] = useState({
    statusSelected: false,
    genderSelected: false,
    speciesSelected: false,
    ageSelected: false,
    outOfExploitationSelected: false,
  })

  const [isAgeActive, setIsAgeActive] = useState(false)
  const [isOutOfExploitationActive, setIsOutOfExploitationActive] =
    useState(false)
  const isGenderActive = selections.gender !== ANIMAL_GENDERS.ALL_GENDERS.label
  const isStatusActive = selections.status !== ANIMAL_STATUS_ENUM.ALL_STATUSES

  const showSpeciesFilter = speciesOptionsWithCount.length >= 2

  const handleFilterSelectedChange = useCallback(
    (filterType: string, isSelected: boolean) => {
      setFilterSelectionState((prev) => {
        const key = `${filterType}Selected`
        // @ts-ignore
        if (prev[key] === isSelected) {
          return prev
        }
        return {
          ...prev,
          [key]: isSelected,
        }
      })
    },
    [],
  )

  const memoizedHandleStatusChange = useCallback(
    (status: string) => {
      handleFilterSelectedChange("status", isStatusActive)
      handleStatusChange(status)
    },
    [handleFilterSelectedChange, handleStatusChange, isStatusActive],
  )

  const memoizedHandleGenderChange = useCallback(
    (gender: string) => {
      handleFilterSelectedChange("gender", isGenderActive)
      handleGenderChange(gender)
    },
    [handleFilterSelectedChange, handleGenderChange, isGenderActive],
  )

  const memoizedHandleAgeChange = useCallback(
    (age: AnimalLifeSpan) => {
      const isSelected = !isAgeActive
      setIsAgeActive(isSelected)
      handleFilterSelectedChange("age", isSelected)
      handleAgeChange(age)
    },
    [handleAgeChange, handleFilterSelectedChange, isAgeActive],
  )

  const memoizedHandleSpeciesChange = useCallback(
    (species: string) => {
      const isSelected = !filterSelectionState.speciesSelected
      handleFilterSelectedChange("species", isSelected)
      handleSpeciesChange(species)
    },
    [
      filterSelectionState.speciesSelected,
      handleFilterSelectedChange,
      handleSpeciesChange,
    ],
  )

  const memoizedHandleOutOfExploitationChange = useCallback(
    (inFarm: boolean) => {
      const isSelected = !isOutOfExploitationActive
      setIsOutOfExploitationActive(isSelected)
      handleFilterSelectedChange("outOfExploitation", isSelected)
      handleOutOfExploitationChange && handleOutOfExploitationChange(inFarm)
    },
    [
      handleFilterSelectedChange,
      handleOutOfExploitationChange,
      isOutOfExploitationActive,
    ],
  )

  const StatusFilterComponent = useMemo(
    () => (
      <SelectionRowDrawer
        multiple={false}
        title={t("status")}
        renderTrigger={
          <SelectPill isActive={isStatusActive}>
            {t(selections.status)}
          </SelectPill>
        }
        options={animalStatusesWithCount}
        selectedValues={selections.status}
        onSelectionChange={memoizedHandleStatusChange}
      />
    ),
    [
      t,
      isStatusActive,
      selections.status,
      animalStatusesWithCount,
      memoizedHandleStatusChange,
    ],
  )

  const GenderFilterComponent = useMemo(
    () => (
      <SelectionRowDrawer
        title={t("animalGender")}
        multiple={false}
        renderTrigger={
          <SelectPill isActive={isGenderActive}>
            {t(selections.gender)}
          </SelectPill>
        }
        options={animalGendersWithCount}
        selectedValues={selections.gender}
        onSelectionChange={memoizedHandleGenderChange}
      />
    ),
    [
      t,
      isGenderActive,
      selections.gender,
      animalGendersWithCount,
      memoizedHandleGenderChange,
    ],
  )

  const AgeFilterComponent = useMemo(
    () => (
      <AnimalAgeDrawer
        selectedAge={selections.age}
        onAgeSelection={memoizedHandleAgeChange}
      />
    ),
    [selections.age, memoizedHandleAgeChange],
  )

  const OutOfExploitationFilterComponent = useMemo(
    () => (
      <CustomToggle
        title={"outOfExploitation"}
        isSelected={selections.outOfExploitation}
        onChange={memoizedHandleOutOfExploitationChange}
      />
    ),
    [selections.outOfExploitation, memoizedHandleOutOfExploitationChange],
  )

  const SpeciesFilterComponent = useMemo(() => {
    if (!showSpeciesFilter) return null
    const selectedSpecies = speciesOptionsWithCount.find(
      (species) => species.value == selections.species,
    )

    return (
      <SelectionRowDrawer
        title={t("species")}
        multiple={false}
        renderTrigger={
          <SelectPill isActive={selectedSpecies?.label !== ALL_SPECIES.label}>
            {t(selectedSpecies?.label || ALL_SPECIES.label)}
          </SelectPill>
        }
        options={speciesOptionsWithCount}
        selectedValues={selections.species}
        onSelectionChange={memoizedHandleSpeciesChange}
      />
    )
  }, [
    showSpeciesFilter,
    t,
    selections.species,
    speciesOptionsWithCount,
    memoizedHandleSpeciesChange,
  ])

  const filterComponents = useMemo(() => {
    const components = [
      {
        key: "status",
        selected: filterSelectionState.statusSelected,
        component: StatusFilterComponent,
      },
      {
        key: "gender",
        selected: filterSelectionState.genderSelected,
        component: GenderFilterComponent,
      },
      {
        key: "age",
        selected: filterSelectionState.ageSelected,
        component: AgeFilterComponent,
      },
      {
        key: "species",
        selected: filterSelectionState.speciesSelected,
        component: SpeciesFilterComponent,
      },
      {
        key: "outOfExploitation",
        selected: filterSelectionState.outOfExploitationSelected,
        component: OutOfExploitationFilterComponent,
      },
    ]

    return components
      .filter((filter) => filter.component !== null)
      .sort((a, b) => (b.selected ? 1 : 0) - (a.selected ? 1 : 0))
  }, [
    filterSelectionState.statusSelected,
    filterSelectionState.genderSelected,
    filterSelectionState.ageSelected,
    filterSelectionState.speciesSelected,
    filterSelectionState.outOfExploitationSelected,
    StatusFilterComponent,
    GenderFilterComponent,
    AgeFilterComponent,
    SpeciesFilterComponent,
    OutOfExploitationFilterComponent,
  ])

  return (
    <div className="mb-6">
      {/* Mobile: Stacked layout */}
      <div className="flex flex-col gap-4 lg:hidden">
        {handleSearch && (
          <SearchBarGeneral
            value={selections.searchQuery}
            onChange={(event) => handleSearch(event.target.value)}
            placeholder={t("searchAnimal")}
          />
        )}
        <FilterScroll>
          {filterComponents.map((filter) => (
            <React.Fragment key={filter.key}>{filter.component}</React.Fragment>
          ))}
        </FilterScroll>
      </div>

      {/* Desktop: Optimized for wider container */}
      <div className="hidden lg:flex lg:flex-col lg:gap-4">
        {/* Search and filters on same row for better space usage */}
        {handleSearch && (
          <div className="flex-1">
            <SearchBarGeneral
              value={selections.searchQuery}
              onChange={(event) => handleSearch(event.target.value)}
              placeholder={t("searchAnimal")}
            />
          </div>
        )}

        <div className="flex flex-1 flex-wrap items-center gap-2">
          {filterComponents.map((filter) => (
            <React.Fragment key={filter.key}>{filter.component}</React.Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}
